/**
 * ProviderHealthBanner — Owner/Admin-only dashboard banner.
 *
 * Polls the admin-only /api/settings/health endpoint and surfaces a calm,
 * restrained banner when one or more provider integrations are not ready.
 * Designed for enterprise tone: no pulsing, no alarms — a single subtle
 * indicator that intelligence services may be partially unavailable.
 *
 * Visibility rules:
 *   - Hidden for Operators, Viewers, Guests (they can't read the endpoint).
 *   - Hidden when status === 'ok'.
 *   - Hidden if the endpoint returns 401/403 (silent suppression).
 *   - Renders a softer "could not be checked" notice if the endpoint errors
 *     for an admin (network/5xx).
 *   - Never blocks dashboard render: it loads independently.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProviderHealthBanner({ onOpenSettings }) {
  const { hasPermission } = useAuth();
  const canSee = hasPermission('integration:view') || hasPermission('integration:manage');

  const [state, setState] = useState({ kind: 'idle' });

  useEffect(() => {
    if (!canSee) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/settings/health`);
        if (cancelled) return;
        if (res.status === 401 || res.status === 403) {
          // Not authorised → suppress entirely.
          setState({ kind: 'hidden' });
          return;
        }
        if (!res.ok) {
          setState({ kind: 'soft-error' });
          return;
        }
        const data = await res.json();
        if (data?.status === 'degraded') setState({ kind: 'degraded' });
        else setState({ kind: 'ok' });
      } catch {
        if (!cancelled) setState({ kind: 'soft-error' });
      }
    })();
    return () => { cancelled = true; };
  }, [canSee]);

  if (!canSee) return null;
  if (state.kind === 'idle' || state.kind === 'hidden' || state.kind === 'ok') return null;

  const isDegraded = state.kind === 'degraded';
  const tone = isDegraded
    ? { bg: '#fef3c7', border: '#fde68a', dot: '#b45309', text: '#92400e' }
    : { bg: '#f1f5f9', border: '#e2e8f0', dot: '#64748b', text: '#475569' };

  const message = isDegraded
    ? 'Provider configuration requires attention. Some intelligence services may be unavailable.'
    : 'Provider health could not be checked. Please review Settings.';

  return (
    <div
      data-testid={isDegraded ? 'provider-health-banner-degraded' : 'provider-health-banner-soft-error'}
      role="status"
      aria-live="polite"
      className={`qf-glass-banner qf-glass-banner--${isDegraded ? 'amber' : 'neutral'}`}
      style={{
        margin: '8px 0 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 13,
        color: tone.text,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 10, height: 10, borderRadius: '50%',
          background: tone.dot, flex: '0 0 auto',
          boxShadow: `0 0 0 3px ${tone.bg}, 0 0 0 4px ${tone.dot}33`,
        }}
      />
      <span style={{ flex: 1, fontWeight: 500 }}>{message}</span>
      <button
        type="button"
        data-testid="provider-health-banner-link"
        onClick={() => { if (typeof onOpenSettings === 'function') onOpenSettings(); }}
        className="qf-glass-banner-action"
        style={{
          color: tone.text,
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        Open Settings → Provider Health
      </button>
    </div>
  );
}
